import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar, Users, CheckCircle } from "lucide-react";

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `linear-gradient(to bottom, rgba(214, 64, 69, 0.85), rgba(26, 26, 26, 0.95)), url('https://images.unsplash.com/photo-1599409091912-88526846d833?w=1920&q=80')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        
        <div className="relative z-10 px-4 md:px-6 py-6 md:py-8">
          {/* Header */}
          <header className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-white rounded-sm flex items-center justify-center">
                <span className="text-primary font-heading font-black text-lg md:text-xl">D</span>
              </div>
              <span className="text-white font-heading font-bold text-base md:text-xl tracking-tight">
                DRAKE TENNIS
              </span>
            </div>
            <div className="flex gap-2 md:gap-3">
              <Link to="/login">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-2 border-white text-white hover:bg-white hover:text-primary font-bold uppercase tracking-wider text-xs md:text-sm"
                  data-testid="login-btn"
                >
                  Accedi
                </Button>
              </Link>
              <Link to="/register">
                <Button 
                  size="sm"
                  className="bg-white text-primary hover:bg-white/90 font-bold uppercase tracking-wider text-xs md:text-sm"
                  data-testid="register-btn"
                >
                  Registrati
                </Button>
              </Link>
            </div>
          </header>

          {/* Hero Content */}
          <div className="max-w-7xl mx-auto mt-12 md:mt-24 pb-16 md:pb-32">
            <div className="max-w-2xl">
              <p className="text-white/80 font-medium uppercase tracking-widest mb-2 md:mb-4 text-xs md:text-base">
                Drake Tennis Academy
              </p>
              <h1 className="text-white font-heading font-black text-4xl sm:text-5xl md:text-7xl lg:text-8xl tracking-tighter uppercase leading-none mb-4 md:mb-6">
                PRENOTA<br />LA TUA<br />LEZIONE
              </h1>
              <p className="text-white/80 text-sm md:text-lg mb-6 md:mb-8 max-w-lg">
                Sistema di prenotazione semplice e veloce per le tue lezioni di tennis.
                Individuale o di gruppo, scegli la disponibilità che preferisci.
              </p>
              <Link to="/register">
                <Button 
                  size="lg"
                  className="bg-white text-primary hover:bg-white/90 font-bold uppercase tracking-wider text-sm md:text-lg px-6 md:px-8 py-4 md:py-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.3)] w-full sm:w-auto"
                  data-testid="cta-register-btn"
                >
                  Inizia Ora
                  <ArrowRight className="ml-2 h-4 w-4 md:h-5 md:w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-12 md:py-24 px-4 md:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading font-bold text-2xl md:text-4xl text-secondary uppercase tracking-tight mb-8 md:mb-16 text-center">
            Come Funziona
          </h2>
          
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-8">
            {/* Feature 1 */}
            <div className="p-6 md:p-8 bg-muted border-l-4 border-primary">
              <div className="w-12 h-12 md:w-14 md:h-14 bg-primary/10 rounded-sm flex items-center justify-center mb-4 md:mb-6">
                <Calendar className="w-6 h-6 md:w-7 md:h-7 text-primary" />
              </div>
              <h3 className="font-heading font-bold text-xl md:text-2xl text-secondary mb-2 md:mb-3 uppercase">
                Scegli Orario
              </h3>
              <p className="text-muted-foreground text-sm md:text-base">
                Visualizza le disponibilità del coach e scegli l'orario più adatto a te.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="p-6 md:p-8 bg-muted border-l-4 border-primary">
              <div className="w-12 h-12 md:w-14 md:h-14 bg-primary/10 rounded-sm flex items-center justify-center mb-4 md:mb-6">
                <Users className="w-6 h-6 md:w-7 md:h-7 text-primary" />
              </div>
              <h3 className="font-heading font-bold text-xl md:text-2xl text-secondary mb-2 md:mb-3 uppercase">
                Prenota
              </h3>
              <p className="text-muted-foreground text-sm md:text-base">
                Lezioni singole o di gruppo. Prenota con un click e attendi la conferma.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="p-6 md:p-8 bg-muted border-l-4 border-primary sm:col-span-2 md:col-span-1">
              <div className="w-12 h-12 md:w-14 md:h-14 bg-primary/10 rounded-sm flex items-center justify-center mb-4 md:mb-6">
                <CheckCircle className="w-6 h-6 md:w-7 md:h-7 text-primary" />
              </div>
              <h3 className="font-heading font-bold text-xl md:text-2xl text-secondary mb-2 md:mb-3 uppercase">
                Gioca
              </h3>
              <p className="text-muted-foreground text-sm md:text-base">
                Una volta confermato il campo, sei pronto per la tua lezione di tennis.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-secondary text-white py-8 md:py-12 px-4 md:px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4 md:gap-6">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-white rounded-sm flex items-center justify-center">
              <span className="text-primary font-heading font-black text-lg md:text-xl">D</span>
            </div>
            <span className="font-heading font-bold text-base md:text-xl tracking-tight">
              DRAKE TENNIS ACADEMY
            </span>
          </div>
          <p className="text-white/60 text-xs md:text-sm">
            © 2024 Drake Tennis Academy. Tutti i diritti riservati.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
